<template>
  <div class="training-zone-wrapper">
    <customer-appointment-home />

    <div class="segmented-control mb-2">
      <div class="segment-btn" :class="{ active: activeTab === 0 }" @click="activeTab = 0">
        <v-icon small class="mr-1" :color="activeTab === 0 ? 'white' : 'grey'">mdi-account-group</v-icon>
        Kurse
      </div>
      <div class="segment-btn" :class="{ active: activeTab === 1 }" @click="activeTab = 1">
        <v-icon small class="mr-1" :color="activeTab === 1 ? 'white' : 'grey'">mdi-dumbbell</v-icon>
        Mein Plan
      </div>
      <div class="segment-slider" :style="{ transform: activeTab === 0 ? 'translateX(0%)' : 'translateX(100%)' }"></div>
    </div>
    <v-card v-if="showAlibeyHint" class="alibey-hint-card mb-3" flat>
      <div class="alibey-hint-icon-wrapper">
        <v-icon color="white" size="22">mdi-clock-outline</v-icon>
      </div>
      <div class="alibey-hint-content">
        <div class="alibey-hint-title">Kursplan in Vorbereitung</div>
        <div class="alibey-hint-subtitle">Hier siehst du eine Vorschau ab {{ $moment(alibeyStartDate).format("DD.MM.") }}</div>
      </div>
    </v-card>
    <v-tabs-items v-model="activeTab" class="transparent-bg" style="padding-bottom: 100px; overflow: visible" touchless>
      <v-tab-item style="overflow: visible">
        <v-skeleton-loader
          v-if="loadingCourses"
          dark
          height="150"
          type="list-item-avatar-two-line"
          class="rounded-xl custom-skeleton mb-2"></v-skeleton-loader>

        <div v-else>
          <div v-if="visibleCourseList.length > 0 && studioCode !== 'berg'">
            <transition-group name="staggered-fade" tag="div">
              <div v-for="(course, i) in visibleCourseList" :key="course._id" class="mb-3 course-item-wrapper" :style="{ '--index': i }">
                <v-card
                  class="rounded-xl gf-list-card d-flex align-center pr-4"
                  :class="{ 'signed-up-border': course.signedUp }"
                  @click="pushToCourse(course)"
                  v-ripple
                  style="position: relative; overflow: visible">
                  <v-chip
                    v-if="course.signedUp && !course.onQueue"
                    small
                    color="primary"
                    style="position: absolute; height: 20px; top: -10px; left: 50%; transform: translateX(-50%); z-index: 5"
                    class="elevation-5">
                    <span style="text-transform: uppercase; font-weight: 900; font-style: italic; font-size: 0.7rem; letter-spacing: 1px"
                      >Angemeldet</span
                    >
                  </v-chip>

                  <v-chip
                    v-if="course.onQueue"
                    small
                    color="orange"
                    style="position: absolute; height: 20px; top: -10px; left: 50%; transform: translateX(-50%); z-index: 5"
                    class="elevation-5">
                    <span
                      style="color: white; text-transform: uppercase; font-weight: 900; font-style: italic; font-size: 0.7rem; letter-spacing: 1px"
                      >Warteliste</span
                    >
                  </v-chip>

                  <v-img
                    :src="course.course.bild ? '/img/kurse/' + course.course.bild : '/img/kurse/bodypump.jpg'"
                    height="85"
                    width="85"
                    class="rounded-l-xl mr-4"
                    style="max-width: 85px"
                    cover>
                    <div class="fill-height d-flex align-end justify-start pa-0">
                      <div class="time-badge">{{ course.duration }} Min</div>
                    </div>
                  </v-img>

                  <div class="flex-grow-1 py-2 overflow-hidden">
                    <div class="d-flex align-center mb-1">
                      <v-chip x-small label color="rgba(82, 174, 50, 0.2)" class="primary--text font-weight-bold px-2 mr-2">
                        {{ courseStartDate(course) }} Uhr
                      </v-chip>
                    </div>

                    <h4 class="white--text font-weight-bold text-uppercase text-body-1 leading-tight text-truncate mb-1">
                      {{ course.course.name }}
                    </h4>

                    <div class="d-flex align-center caption grey--text text--lighten-1">
                      <v-icon x-small color="grey" class="mr-1">mdi-account-outline</v-icon>
                      <span class="text-truncate">
                        {{ getInstructorName(course) }}
                      </span>
                    </div>
                  </div>

                  <div class="d-flex flex-column align-center ml-2">
                    <div class="capacity-ring">
                      <v-progress-circular
                        :value="calcPercentage(course)"
                        :color="getIndicatorColor(calcPercentage(course))"
                        size="44"
                        width="3"
                        rotate="-90">
                        <span class="capacity-text" style="font-size: 0.75rem"> {{ course.signupAmount || 0 }}/{{ course.amount || 0 }} </span>
                      </v-progress-circular>
                    </div>
                  </div>
                </v-card>
              </div>
            </transition-group>

            <div class="text-center mt-4">
              <v-btn text small color="grey lighten-1" @click="$router.push('/kurse')">
                Zum kompletten Plan <v-icon right x-small>mdi-chevron-right</v-icon>
              </v-btn>
            </div>
          </div>
          <div v-else-if="studioCode === 'berg'" class="text-center pa-8 glass-card rounded-xl preparation-state">
            <div class="icon-pulse-wrapper mb-4">
              <v-icon size="50" color="primary">mdi-hammer-wrench</v-icon>
            </div>
            <h3 class="white--text font-weight-black text-h6 mb-2">Hier entsteht Grosses!</h3>
            <p class="caption grey--text text--lighten-1 mb-0 px-4">
              Wir tüfteln gerade an einem brandneuen Kursplan für dich. <br />
              Bald erfährst du hier alle Details!
            </p>

            <div class="glow-bg"></div>
          </div>
          <div v-else class="text-center pa-10 glass-card rounded-xl">
            <v-icon size="60" color="grey darken-2" class="mb-4">mdi-calendar-sleep</v-icon>
            <h4 class="white--text font-weight-bold">Feierabend für heute!</h4>
            <p class="caption grey--text mb-6">Für heute sind keine Kurse mehr geplant.</p>
            <v-btn rounded outlined color="primary" @click="$router.push('/kurse')">Morgen checken</v-btn>
          </div>
        </div>
      </v-tab-item>

      <v-tab-item>
        <v-skeleton-loader v-if="loadingWorkout" dark height="150" type="image" class="rounded-xl custom-skeleton"></v-skeleton-loader>

        <div v-else>
          <div v-if="activeSplit && workoutStartTime" class="mb-5">
            <v-card class="rounded-xl live-card overflow-hidden relative" height="120" @click="$router.push('/workout/run/split')" v-ripple>
              <div class="live-card-glow"></div>
              <v-img
                :src="getVisualization(activeSplit.title).img"
                class="fill-height"
                gradient="to right, rgba(15,15,15,0.95) 25%, rgba(15,15,15,0.6)">
                <div class="d-flex align-center fill-height pl-4 pr-3 relative z-index-2">
                  <div class="avatar-radar mr-4">
                    <v-avatar size="64" class="rounded-lg elevation-5" style="border: 2px solid #fff">
                      <v-img :src="getVisualization(activeSplit.title).img"></v-img>
                    </v-avatar>
                    <div class="radar-ring"></div>
                  </div>
                  <div class="flex-grow-1">
                    <div class="d-flex align-center mb-1">
                      <v-icon x-small color="red accent-2" class="mr-1 blink-text">mdi-record</v-icon>
                      <span class="caption font-weight-bold red--text text--accent-2 spacing-1 blink-text">LIVE RECORDING</span>
                    </div>
                    <h3 class="white--text font-weight-black text-h6 text-truncate mb-1">{{ activeSplit.title }}</h3>
                    <div class="d-flex align-center">
                      <v-chip x-small color="rgba(255,255,255,0.1)" class="white--text font-weight-bold font-mono">
                        {{ elapsedTime }}
                      </v-chip>
                    </div>
                  </div>
                  <v-btn icon color="white" class="glass-btn-circle"><v-icon>mdi-chevron-right</v-icon></v-btn>
                </div>
              </v-img>
            </v-card>
          </div>

          <div v-if="workout && workout.splits && workout.splits.workouts.length > 0">
            <h4 class="white--text font-weight-bold mb-3 pl-1 d-flex align-center">
              <v-icon small color="primary" class="mr-2">mdi-view-grid-outline</v-icon>
              Dein Split
            </h4>

            <v-row no-gutters>
              <v-col cols="6" v-for="(split, idx) in workout.splits.workouts" :key="idx" class="pb-3 px-2">
                <v-card class="rounded-xl split-card-enhanced overflow-hidden elevation-5" height="150" @click="handleSplitClick(split)" v-ripple>
                  <v-img
                    :src="getVisualization(split.title).img"
                    class="fill-height"
                    gradient="to top, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.5) 50%, rgba(0,0,0,0.2) 100%">
                    <div class="d-flex justify-space-between pa-3 relative z-index-2">
                      <div class="glass-icon-badge">
                        <v-icon x-small color="white">mdi-dumbbell</v-icon>
                      </div>
                    </div>

                    <div class="split-content-bottom pa-3">
                      <h4 class="white--text font-weight-black text-uppercase text-body-1 leading-tight text-truncate text-shadow-strong mb-2">
                        {{ split.title }}
                      </h4>

                      <div class="split-glass-bar d-flex align-center justify-space-between px-2 py-1 rounded-lg">
                        <span class="caption white--text font-weight-bold" style="font-size: 0.7rem">
                          {{ split.exercises ? split.exercises.length : 0 }} Übungen
                        </span>
                        <v-icon x-small color="primary">mdi-play</v-icon>
                      </div>
                    </div>
                  </v-img>
                </v-card>
              </v-col>
            </v-row>

            <div class="text-center mt-3">
              <v-btn text small color="grey" @click="$router.push('/workout/hub')"> Plan bearbeiten <v-icon right x-small>mdi-pencil</v-icon> </v-btn>
            </div>
          </div>

          <v-card v-else class="rounded-xl glass-card pa-6 text-center" @click="$router.push('/workout/hub')">
            <v-icon size="56" color="grey darken-1" class="mb-3">mdi-rocket-launch-outline</v-icon>
            <h3 class="white--text font-weight-bold">Starte durch!</h3>
            <p class="caption grey--text mb-6">Erstelle deinen ersten Trainingsplan.</p>
            <v-btn rounded color="primary" class="font-weight-bold elevation-10 px-6">Plan erstellen</v-btn>
          </v-card>
        </div>
      </v-tab-item>
    </v-tabs-items>

    <v-dialog v-model="showContinueDialog" persistent max-width="420" content-class="rounded-xl glass-dialog">
      <v-card class="glass-card pa-4 text-center">
        <v-avatar
          size="70"
          class="mb-4 elevation-10"
          :style="{ border: '3px solid ' + getVisualization(activeSplit ? activeSplit.title : '').color }">
          <v-img :src="getVisualization(activeSplit ? activeSplit.title : '').img"></v-img>
        </v-avatar>
        <h3 class="white--text font-weight-black text-h5 mb-2">Training aktiv!</h3>
        <p class="body-2 grey--text text--lighten-1 mb-6 px-2">
          Du hast bereits ein laufendes Training. Möchtest du es fortsetzen oder das Neue starten?
        </p>

        <v-btn block x-large rounded color="primary" class="mb-3 font-weight-bold" @click="$router.push('/workout/run/split')">
          Training Fortsetzen
        </v-btn>
        <v-btn block text color="grey lighten-1" @click="discardAndStartNew"> Verwerfen & Neues starten </v-btn>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import api from "@/api"
import moment from "moment"
import { mapState, mapGetters, mapActions } from "vuex"
import userService from "@/services/userService"
import CustomerAppointmentHome from "./CustomerAppointmentHome.vue"
export default {
  name: "TrainingZone",
  components: {
    CustomerAppointmentHome,
  },
  data() {
    return {
      userService,
      activeTab: 0,
      loadingCourses: true,
      visibleCourseList: [],
      abortController: null,
      loadingWorkout: true,
      timerInterval: null,
      elapsedTime: "00:00",
      showContinueDialog: false,
      pendingSplit: null,
    }
  },
  computed: {
    ...mapState("runWorkoutStore", ["activeSplit", "workoutStartTime"]),
    ...mapGetters("runWorkoutStore", { workout: "hubWorkoutPlan" }),
    ...mapState("userStore", ["user"]),
    ...mapState("studioStore", ["selectedStudio"]),
    alibeyStudio() {
      const code = this.selectedStudio?.code?.toLowerCase()
      return code === "alibey" || code === "ali"
    },
    studioCode() {
      const code = this.selectedStudio?.code?.toLowerCase()
      return code
    },

    alibeyStartDate() {
      return "2026-05-30"
    },

    alibeyEndDate() {
      return "2026-06-05"
    },
    showAlibeyHint() {
      return false
      if (!this.alibeyStudio) return false
      if (this.userService.hasRight("gf_f_courses")) return false
      // Nur vor Start des Zeitraums anzeigen
      return this.$moment().isBefore(this.$moment(this.alibeyStartDate, "YYYY-MM-DD"))
    },
    sirnachStudio() {
      return this.user && this.user.studio_id === "sirnach"
    },
  },
  async created() {
    this.abortController = new AbortController()
    this.fetchCourses().finally(() => (this.loadingCourses = false))
    try {
      await this.loadHubData()
    } catch (e) {
      console.log("No workout plan found", e)
    } finally {
      this.loadingWorkout = false
    }
    this.startTimer()
  },
  beforeDestroy() {
    if (this.timerInterval) clearInterval(this.timerInterval)
    if (this.abortController) this.abortController.abort()
  },
  methods: {
    ...mapActions("runWorkoutStore", ["loadHubData", "setActiveSplit", "setActivePlanID", "resetWorkoutState"]),

    async fetchCourses() {
      try {
        let targetTs

        if (this.alibeyStudio) {
          // Bei Alibey: bestimme welchen Tag wir laden
          const today = this.$moment().startOf("day")
          const alibeyStart = this.$moment(this.alibeyStartDate, "YYYY-MM-DD").startOf("day")
          const alibeyEnd = this.$moment(this.alibeyEndDate, "YYYY-MM-DD").startOf("day")

          let targetDate
          if (today.isBefore(alibeyStart)) {
            // Vor dem Start: lade ersten Tag
            targetDate = alibeyStart
          } else if (today.isAfter(alibeyEnd)) {
            // Nach dem Ende: lade letzten Tag
            targetDate = alibeyEnd
          } else {
            // Im Zeitraum: lade aktuellen Tag
            targetDate = today
          }

          targetTs = targetDate.unix()
        } else {
          // Standard: heute
          targetTs = this.$moment().set({ hours: 0, minutes: 0, seconds: 0 }).unix()
        }

        const data = await api.fetchCourseList(this.abortController.signal, targetTs)

        // Filter: bei Alibey kein "isBefore end"-Filter, da wir ja bewusst einen anderen Tag laden
        if (this.alibeyStudio) {
          this.visibleCourseList = data.filter((c) => !c.cancelled)
        } else {
          const now = this.$moment()
          this.visibleCourseList = data.filter((c) => {
            const end = this.$moment().set({ hours: c.hours, minutes: c.minutes }).add(c.duration, "minutes")
            return !c.cancelled && now.isBefore(end)
          })
        }
      } catch (e) {
        if (e.name !== "AbortError") console.error(e)
      }
    },
    courseStartDate(course) {
      return this.$moment().set({ hours: course.hours, minutes: course.minutes }).format("HH:mm")
    },
    getInstructorName(course) {
      if (course.instructors && course.instructors.length > 0) {
        return course.instructors[0].vorname
      }
      return "Team"
    },
    pushToCourse(course) {
      if (this.showAlibeyHint) return
      if (this.sirnachStudio) return this.$router.push("/kurse")

      // Bei Alibey kein "Past"-Check (Kurse sind ja in der Zukunft)
      if (!this.alibeyStudio) {
        const courseEndTime = this.$moment().set({ hours: course.hours, minutes: course.minutes }).add(course.duration, "minutes")
        if (this.$moment().isAfter(courseEndTime)) return
      }

      // TS bestimmen (wie in fetchCourses)
      let targetTs
      if (this.alibeyStudio) {
        const today = this.$moment().startOf("day")
        const alibeyStart = this.$moment(this.alibeyStartDate, "YYYY-MM-DD").startOf("day")
        const alibeyEnd = this.$moment(this.alibeyEndDate, "YYYY-MM-DD").startOf("day")

        if (today.isBefore(alibeyStart)) targetTs = alibeyStart.unix()
        else if (today.isAfter(alibeyEnd)) targetTs = alibeyEnd.unix()
        else targetTs = today.unix()
      } else {
        targetTs = this.$moment().set({ hours: 0, minutes: 0, seconds: 0 }).unix()
      }

      let ts = course.courseTimelineId ? 0 : targetTs

      this.$router.push({
        name: "courseListView",
        params: { id: course._id, ts },
        query: { home: true },
      })
    },
    calcPercentage(course) {
      if (course.amount > 0) {
        // Alte Logik: Freie Plätze berechnen
        const free = course.amount - (course.signupAmount || 0)
        // Prozent der FREIEN Plätze (für die Farbe/Kreis)
        return Math.round((free / course.amount) * 100)
      }
      return 100
    },
    // Farbe passend dazu (wenig Platz = Rot)
    getIndicatorColor(percentage) {
      if (percentage <= 10) return "red"
      if (percentage <= 50) return "orange"
      return "primary"
    },
    getVisualization(title) {
      if (!title) return { img: "/img/workout/upperBodySplit.png", color: "#52ae32" }
      const lowerName = title.toLowerCase()
      if (lowerName.includes("bein") || lowerName.includes("leg")) return { img: "/img/workout/legSplit.png", color: "#1C4966" }
      if (lowerName.includes("brust") || lowerName.includes("push")) return { img: "/img/workout/chestSplit.png", color: "#02734A" }
      if (lowerName.includes("rücken") || lowerName.includes("pull")) return { img: "/img/workout/backSplit.png", color: "#F2B950" }
      if (lowerName.includes("arm")) return { img: "/img/workout/armSplit.png", color: "#E5735D" }
      return { img: "/img/workout/upperBodySplit.png", color: "#A3C6C4" }
    },
    handleSplitClick(split) {
      if (this.activeSplit && this.workoutStartTime) {
        this.pendingSplit = split
        this.showContinueDialog = true
      } else {
        this.startSplit(split)
      }
    },
    async startSplit(split) {
      await this.setActivePlanID(this.workout._id)
      await this.setActiveSplit(split)
      this.$router.push("/workout/run/split")
    },
    async discardAndStartNew() {
      await this.resetWorkoutState()
      this.showContinueDialog = false
      if (this.pendingSplit) {
        this.startSplit(this.pendingSplit)
        this.pendingSplit = null
      }
    },
    startTimer() {
      this.timerInterval = setInterval(() => {
        if (this.workoutStartTime) {
          const duration = moment.duration(moment().diff(this.workoutStartTime))
          const hours = Math.floor(duration.asHours())
          const minutes = String(duration.minutes()).padStart(2, "0")
          const seconds = String(duration.seconds()).padStart(2, "0")
          this.elapsedTime = hours > 0 ? `${hours}:${minutes}:${seconds}` : `${minutes}:${seconds}`
        }
      }, 1000)
    },
  },
}
</script>

<style lang="scss" scoped>
.alibey-hint-card {
  display: flex;
  align-items: center;
  background: linear-gradient(135deg, rgba(82, 174, 50, 0.18) 0%, rgba(82, 174, 50, 0.08) 100%) !important;
  border: 1px solid rgba(82, 174, 50, 0.3) !important;
  border-radius: 14px !important;
  padding: 12px 14px;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: -30px;
    right: -30px;
    width: 100px;
    height: 100px;
    background: rgba(82, 174, 50, 0.1);
    border-radius: 50%;
  }
}

.alibey-hint-icon-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  min-width: 40px;
  border-radius: 12px;
  background: linear-gradient(135deg, var(--v-primary) 0%, #00b84c 100%);
  margin-right: 12px;
  box-shadow: 0 4px 12px rgba(82, 174, 50, 0.3);
  position: relative;
  z-index: 1;

  .v-icon {
    animation: pulse-icon 2s ease-in-out infinite;
  }
}

.alibey-hint-content {
  flex: 1;
  position: relative;
  z-index: 1;
}

.alibey-hint-title {
  font-size: 0.9rem;
  font-weight: 800;
  color: white;
  letter-spacing: 0.3px;
  line-height: 1.2;
}

.alibey-hint-subtitle {
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.65);
  margin-top: 2px;
}

@keyframes pulse-icon {
  0%,
  100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
}
/* VARIABLES */
$primary-color: var(--v-primary, #52ae32);
$glass-bg: rgba(255, 255, 255, 0.05);
$glass-border: rgba(255, 255, 255, 0.08);

.signed-up-border {
  border-left: 4px solid $primary-color !important;
  /* Optional: Leichter Background-Glow für angemeldete Kurse */
  background: linear-gradient(90deg, rgba(82, 174, 50, 0.15) 0%, rgba(255, 255, 255, 0.04) 30%) !important;
}

.italic-header {
  font-style: italic;
  letter-spacing: -0.5px;
  text-shadow: 0 4px 15px rgba(0, 0, 0, 0.6);
  text-transform: uppercase;
}
.transparent-bg {
  background: transparent !important;
}
.relative {
  position: relative;
}
.z-index-2 {
  z-index: 2;
}

/* TABS */
.segmented-control {
  position: relative;
  display: flex;
  background: rgba(0, 0, 0, 0.3);
  border-radius: 12px;
  padding: 4px;
  height: 48px;
  border: 1px solid rgba(255, 255, 255, 0.05);
}
.segment-btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.9rem;
  color: #888;
  z-index: 2;
  cursor: pointer;
  transition: color 0.3s;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.segment-btn.active {
  color: white;
}
.segment-slider {
  position: absolute;
  top: 4px;
  left: 4px;
  width: calc(50% - 4px);
  height: calc(100% - 8px);
  background: $primary-color;
  border-radius: 9px;
  z-index: 1;
  transition: transform 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  box-shadow: 0 2px 10px rgba(82, 174, 50, 0.4);
}

/* COURSE CARD */
.gf-list-card {
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.05);
  transition: all 0.2s;
  &:active {
    background: rgba(255, 255, 255, 0.08);
    transform: scale(0.98);
  }
}
.time-badge {
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(4px);
  color: white;
  font-size: 0.65rem;
  font-weight: bold;
  padding: 2px 8px;
  border-top-right-radius: 10px;
}
.capacity-text {
  font-size: 0.9rem;
  font-weight: 900;
  color: white;
}

/* LIVE CARD */
.live-card {
  border: 1px solid rgba(255, 255, 255, 0.1);
}
.live-card-glow {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  box-shadow: inset 0 0 20px rgba(82, 174, 50, 0.3);
  z-index: 1;
  animation: border-pulse 2s infinite;
}
.avatar-radar {
  position: relative;
}
.radar-ring {
  position: absolute;
  top: -5px;
  left: -5px;
  right: -5px;
  bottom: -5px;
  border: 2px solid $primary-color;
  border-radius: 12px;
  animation: radar 2s infinite;
  opacity: 0;
}
.glass-btn-circle {
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(4px);
}

/* --- SPLIT CARDS 2.0 (THE NEW SEXY) --- */
.split-card-enhanced {
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: transform 0.2s, box-shadow 0.2s;
  position: relative;

  &:active {
    transform: scale(0.96);
  }
}

.split-content-bottom {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  z-index: 2;
}

.glass-icon-badge {
  width: 32px;
  height: 32px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2);
}

.split-glass-bar {
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.text-shadow-strong {
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.9);
}

/* SKELETON FIX */
.custom-skeleton {
  background: transparent !important;
}
/* Aggressives Überschreiben für Vuetify Skeletons */
::v-deep .v-skeleton-loader__bone {
  background: rgba(255, 255, 255, 0.08) !important;
}
::v-deep .v-skeleton-loader__image,
::v-deep .v-skeleton-loader__avatar,
::v-deep .v-skeleton-loader__text {
  background: rgba(255, 255, 255, 0.08) !important;
}
/* Falls Vuetify Theme Light ist, erzwingen wir hier dunkle Farben */
::v-deep .theme--light.v-skeleton-loader .v-skeleton-loader__bone:after {
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.05), transparent) !important;
}

/* Animations */
.course-item-wrapper {
  animation: slideIn 0.4s ease-out forwards;
  opacity: 0;
  transform: translateY(10px);
  animation-delay: calc(var(--index) * 0.05s);
}
@keyframes slideIn {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
.blink-text {
  animation: blink 1.5s infinite;
}
@keyframes blink {
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0.4;
  }
  100% {
    opacity: 1;
  }
}
@keyframes border-pulse {
  0% {
    box-shadow: inset 0 0 10px rgba(82, 174, 50, 0.1);
  }
  50% {
    box-shadow: inset 0 0 25px rgba(82, 174, 50, 0.4);
  }
  100% {
    box-shadow: inset 0 0 10px rgba(82, 174, 50, 0.1);
  }
}
@keyframes radar {
  0% {
    transform: scale(1);
    opacity: 0.8;
  }
  100% {
    transform: scale(1.4);
    opacity: 0;
  }
}

/* Utils */
.glass-card {
  background: $glass-bg !important;
  backdrop-filter: blur(25px);
  border: 1px solid $glass-border;
  box-shadow: 0 10px 40px -10px rgba(0, 0, 0, 0.5);
}
.glass-dialog {
  box-shadow: none;
  overflow: visible;
}
.leading-tight {
  line-height: 1.1;
}
</style>
